import React, {PropsWithChildren, useEffect} from 'react';
import {useLocalStorage} from 'react-use';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {TeamApi, TeamMapper} from 'app/components/entities/team';
import {User, UserMapper, useProfileQuery} from 'app/components/entities/user';
import {WS} from 'app/api/WebSocket/WS';
import {EVENT_TYPE} from 'app/api/WebSocket/constants';
import {Ws} from 'app/contracts/ws';
import {UserAgreement} from 'app/components/features/profile';
import {isUUID} from 'app/util/isUUID';
import {cirrusApi} from 'app/api/CirrusApi';
import {InitPage} from 'app/components/pages/init';
import {apiClient, isAjaxError} from 'app/components/shared';

interface UserProps {
  teamId: string;
}

function ApplicationUser({teamId, children}: PropsWithChildren<UserProps>) {
  const client = useQueryClient();

  const {data} = useProfileQuery(teamId);

  useEffect(() => {
    const onUserUpdate = (message: Ws.UserChange) => {
      client.setQueryData<User.Profile>(['profile'], (prev) =>
        prev ? UserMapper.mapProfile(message.Body.User) : prev,
      );
    };

    WS.on(EVENT_TYPE.USER_CHANGE, onUserUpdate);

    return () => {
      WS.off(EVENT_TYPE.USER_CHANGE, onUserUpdate);
    };
  }, [client, teamId]);

  if (data) {
    const showAgreement = !data.agreement;

    return (
      <>
        {showAgreement && <UserAgreement />}
        {children}
      </>
    );
  }

  return <InitPage progress={60} />;
}

interface TeamProps {
  teamId: string;
}

function ApplicationTeam({teamId, children}: PropsWithChildren<TeamProps>) {
  const {data, refetch} = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const res = await TeamApi.getTeamDetails(teamId);
      return TeamMapper.mapTeamDetails(res);
    },
  });

  useEffect(() => {
    const onTeamChange = async () => {
      await refetch();
    };

    WS.on(EVENT_TYPE.TEAM_CHANGE, onTeamChange);

    return () => {
      WS.off(EVENT_TYPE.TEAM_CHANGE, onTeamChange);
    };
  }, [refetch]);

  if (data) {
    return <ApplicationUser teamId={data.id}>{children}</ApplicationUser>;
  }

  return <InitPage progress={40} />;
}

export function AppBootstrap({children}: PropsWithChildren<{}>) {
  const urlId = getIdFromUrl();
  const [localId, setLocalId] = useLocalStorage<string>('team-id');

  useEffect(() => {
    apiClient.instance.interceptors.response.use(
      // do not remove it
      (res) => res,
      (err) => {
        if (isAjaxError(err)) {
          switch (err.response?.status) {
            case 401: {
              const url = buildLoginUrl();
              window.location.assign(url);
              break;
            }

            default: {
              console.warn('ajax error', err);
            }
          }
        }
      },
    );
  }, []);

  const {data: teamId} = useQuery({
    queryKey: ['team-id'],
    queryFn: async () => {
      const list = [urlId, localId].filter((id): id is string => Boolean(id));
      const res = await TeamApi.validate(list);
      return res.team_id;
    },
  });

  // For legacy api client and WS connection
  if (teamId) {
    cirrusApi.setCurrentTeamId(teamId);
  }

  useEffect(() => {
    if (teamId) {
      setLocalId(teamId);
    }
  }, [teamId, setLocalId]);

  if (teamId) {
    if (teamId === urlId) {
      return <ApplicationTeam teamId={teamId}>{children}</ApplicationTeam>;
    }

    const url = new URL(window.location.href);
    url.search = '';
    url.pathname = `/${teamId}`;
    window.location.assign(url);
    return <></>;
  }

  return <InitPage progress={20} />;
}

function getIdFromUrl(): string | undefined {
  const id = window.location.pathname.slice(window.location.pathname.lastIndexOf('/') + 1);

  if (isUUID(id)) {
    return id;
  }
}

function buildLoginUrl() {
  const params = new URLSearchParams();
  params.append('next', window.location.pathname + window.location.hash);

  const url = new URL(window.location.origin);
  url.pathname = '/login.html';
  url.hash = `/login?${params.toString()}`;
  return url;
}
